import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { campaignService } from "../../services/campaignService";
import { getPhoneNumbers } from "@/services/phoneNumberService";
import {
  Campaign,
  CampaignStatus,
  CampaignUpdate,
} from "../../types/campaign";
import { PhoneNumber } from "@/models/PhoneNumber";
import { useToast } from "../ui/use-toast";

interface CampaignSettingsFormProps {
  campaign: Campaign;
  onComplete: () => void;
}

export function CampaignSettingsForm({
  campaign,
  onComplete,
}: CampaignSettingsFormProps) {
  const [formData, setFormData] = useState<Partial<CampaignUpdate>>({
    start_date: campaign.start_date,
    end_date: campaign.end_date,
    phone_number_id: campaign.phone_number_id,
  });
  const [loading, setLoading] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchPhoneNumbers = async () => {
      try {
        setLoadingPhoneNumbers(true);
        const numbers = await getPhoneNumbers(campaign.organization_id);
        // Filter to only outbound numbers
        const outboundNumbers = numbers.filter(n => n.type === "outbound");
        setPhoneNumbers(outboundNumbers);
      } catch (error) {
        console.error("Error fetching phone numbers:", error);
        toast({
          title: "Error",
          description: "Failed to load phone numbers",
          variant: "destructive",
        });
      } finally {
        setLoadingPhoneNumbers(false);
      }
    };

    fetchPhoneNumbers();
  }, [campaign.organization_id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value) {
      setFormData((prev) => ({
        ...prev,
        [name]: new Date(value).toISOString(),
      }));
    } else {
      setFormData((prev) => {
        const newData = { ...prev };
        delete newData[name as keyof typeof newData];
        return newData;
      });
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.phone_number_id) {
        toast({
          title: "Phone number required",
          description: "Please select an outbound phone number",
          variant: "destructive",
        });
        return;
      }

      // Update the campaign
      const { data, error } = await campaignService.updateCampaign(
        campaign.id,
        formData as CampaignUpdate,
      );

      if (error) {
        throw error;
      }

      toast({
        title: "Campaign updated",
        description: "Campaign settings have been updated successfully",
      });

      onComplete();
    } catch (error) {
      console.error("Error updating campaign:", error);
      toast({
        title: "Error updating campaign",
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="start_date">Start Date</Label>
          <Input
            id="start_date"
            name="start_date"
            type="date"
            value={
              formData.start_date
                ? new Date(formData.start_date).toISOString().split("T")[0]
                : ""
            }
            onChange={handleDateChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="end_date">End Date</Label>
          <Input
            id="end_date"
            name="end_date"
            type="date"
            value={
              formData.end_date
                ? new Date(formData.end_date).toISOString().split("T")[0]
                : ""
            }
            onChange={handleDateChange}
          />
        </div>
      </div>


      <div className="space-y-2">
        <Label htmlFor="phone_number_id">Outbound Phone Number</Label>
        <Select
          value={formData.phone_number_id}
          onValueChange={(value) => setFormData(prev => ({ ...prev, phone_number_id: value }))}
          disabled={loadingPhoneNumbers}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a phone number" />
          </SelectTrigger>
          <SelectContent>
            {phoneNumbers.map((number) => (
              <SelectItem key={number.id} value={number.id}>
                {number.phone_number}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {phoneNumbers.length === 0 && !loadingPhoneNumbers && (
          <p className="text-xs text-destructive">
            No outbound phone numbers available. Please add a phone number first.
          </p>
        )}
      </div>


      <div className="flex justify-end space-x-2 pt-4">
        <Button type="submit" disabled={loading}>
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </form>
  );
}